import { useQueryClient } from '@tanstack/react-query';
import { createContext, PropsWithChildren, useContext } from 'react';
import { useCurrency } from 'src/common/hooks/useCurrency';
import {
  cleanUpFieldsForCreation,
  UpsertEntityInput,
} from 'src/data-capture/merge-review/useUpsertEntities';
import {
  EntityCategory,
  selectDetectionSelector,
  UpdateAction,
  useDetectedEntitiesStore,
} from 'src/data-capture/stores/useDetectedEntitiesStore';
import { DetectionOwner, EntityFields, FieldValue, GenericEntity } from 'src/data-capture/types';
import {
  CoOwnerCopy,
  describeLikelyCopy,
  SavedOntoCopy,
} from 'src/financial/financial-data/financial-item/likelyCopies';
import { valuationFieldsFor } from 'src/financial/financial-data/financial-item/rework/financialItemRework';
import { useLikelyCopyCheck } from 'src/financial/financial-data/financial-item/useLikelyCopyCheck';
import { EntityFields as UIEntityFields } from 'src/UI/dynamicUI/types';

type LikelyCopyCheck = ReturnType<typeof useLikelyCopyCheck>;

const LikelyCopyCheckContext = createContext<LikelyCopyCheck | null>(null);

/** One likely copy check for the whole review, so its modal is rendered once however many cards can save. */
export function ReviewLikelyCopyCheckProvider({ children }: PropsWithChildren) {
  const likelyCopyCheck = useLikelyCopyCheck();
  return (
    <LikelyCopyCheckContext.Provider value={likelyCopyCheck}>
      {children}
      {likelyCopyCheck.likelyCopiesModal}
    </LikelyCopyCheckContext.Provider>
  );
}

type DetectionToAdd = { detectionId: string; payload: UpsertEntityInput };

const fieldsToSend = (payload: UpsertEntityInput): GenericEntity =>
  cleanUpFieldsForCreation(payload.entityFieldsMetadata, payload.values as UIEntityFields);

// Names a detection the way the adviser sees it on its card, where one check covers several.
const describeDetected = (
  entityType: string,
  fields: GenericEntity,
  currency: ReturnType<typeof useCurrency>,
) => {
  const valueField = valuationFieldsFor(entityType)?.valueField;
  const amount = valueField ? fields[valueField] : null;
  return describeLikelyCopy(
    {
      description: typeof fields.description === 'string' ? fields.description : null,
      provider: typeof fields.provider === 'string' ? fields.provider : null,
      amount: typeof amount === 'number' ? amount : null,
    },
    currency,
  );
};

/**
 * Folding deleted each copy, so its co-owner keeps a place among the owners an undo puts back, or
 * they would be left with no record of the holding. The shares no longer add up once they join, so
 * every share goes back to unrecorded.
 */
export const withFoldedCoOwners = (
  owners: DetectionOwner[],
  folded: CoOwnerCopy[],
): DetectionOwner[] => {
  const ownerIds = new Set(owners.map(owner => owner.client_id));
  const joining = [...new Set(folded.map(copy => copy.coOwnerId))].filter(
    coOwnerId => !ownerIds.has(coOwnerId),
  );
  if (joining.length === 0) return owners;
  return [...owners, ...joining.map(client_id => ({ client_id }))].map(owner => ({
    ...owner,
    ownership_percentage: null,
  }));
};

// Undo sends these back through the review's own save, which reads each field's type from the
// detection's metadata, so only the fields the detection carries are kept, with the owners.
const previousValuesOfCopy = (
  saved: SavedOntoCopy,
  payload: UpsertEntityInput,
  folded: CoOwnerCopy[],
): EntityFields => {
  const previous = Object.fromEntries(
    Object.entries(saved.previous_fields).filter(
      ([field]) => field in payload.entityFieldsMetadata || field === 'owners',
    ),
  );
  const owners = withFoldedCoOwners((previous.owners ?? []) as DetectionOwner[], folded);
  return {
    ...previous,
    owners: owners as unknown as FieldValue,
    id: saved.item_id,
  } as EntityFields;
};

/**
 * Document review's side of the likely copy check in `useLikelyCopyCheck`. A detection added as new
 * is not an item yet, so a co-owner's likely copy the adviser says is the same takes the save in its
 * place. A detection updating one of the client's own items is saved as usual, and the copy is then
 * folded into that item, as saving an item page does.
 */
export function useReviewLikelyCopies() {
  const likelyCopyCheck = useContext(LikelyCopyCheckContext);
  if (likelyCopyCheck === null) {
    throw new Error('useReviewLikelyCopies needs a ReviewLikelyCopyCheckProvider');
  }
  const { chooseSameCopies, chooseSameCopiesForEach, foldCopies, saveOntoCopy } = likelyCopyCheck;
  const currency = useCurrency();
  const queryClient = useQueryClient();

  /** Each detection's copies the adviser says are the same, or null when they go back. */
  const chooseCopiesToSaveOnto = async (
    clientId: string,
    category: EntityCategory,
    detections: DetectionToAdd[],
  ): Promise<Record<string, CoOwnerCopy[]> | null> => {
    const selectDetection = selectDetectionSelector(useDetectedEntitiesStore.getState());
    const saves = detections.map(({ detectionId, payload }) => {
      const detection = selectDetection(category, detectionId);
      const fields = fieldsToSend(payload);
      return {
        product: payload.entityType,
        clientId,
        // The id the detector gave the item, which a co-owner's review may already have saved.
        itemId: detection?.entityId ?? undefined,
        fields,
        label: describeDetected(payload.entityType, fields, currency),
        // Adding the detection as new already says it is not one of the items it conflicts with.
        shownItemIds: detection?.conflicts.map(conflict => conflict.id),
      };
    });
    const chosen = await chooseSameCopiesForEach(saves, 'save-onto-theirs');
    if (chosen === null) return null;
    return Object.fromEntries(
      detections.map(({ detectionId }, index) => [detectionId, chosen[index]]),
    );
  };

  /**
   * One copy takes the save and any other the adviser says is the same is folded into it. The
   * check lets at most one copy that cannot be folded be the same, so that one takes the save.
   * Resolves to the last action that undoes the save.
   */
  const saveOntoCopies = async (
    clientId: string,
    payload: UpsertEntityInput,
    copies: CoOwnerCopy[],
  ): Promise<UpdateAction> => {
    const theirs = copies.find(copy => copy.fold_blocker !== null) ?? copies[0];
    const others = copies.filter(copy => copy !== theirs);
    const saved = await saveOntoCopy(theirs, fieldsToSend(payload));
    const folded = await foldCopies(clientId, saved.item_id, others);
    // The detections themselves are unchanged by the save, so they are not refetched.
    const notDetections = {
      predicate: (query: { queryKey: readonly unknown[] }) =>
        !query.queryKey.includes('detections'),
    };
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['clients', clientId], ...notDetections }),
      queryClient.invalidateQueries({ queryKey: ['clients', theirs.coOwnerId], ...notDetections }),
    ]);
    return {
      type: 'update',
      targetEntityFieldsMetadata: payload.entityFieldsMetadata,
      targetEntityType: payload.entityType,
      previousValues: previousValuesOfCopy(saved, payload, folded),
      savedOntoCopyId: saved.item_id,
    };
  };

  /** The copies to fold into the client's own item once an update to it is saved, or null. */
  const chooseCopiesToFold = (
    clientId: string,
    payload: UpsertEntityInput,
    storedOwners: DetectionOwner[] | undefined,
  ) => {
    const fields = fieldsToSend(payload);
    return chooseSameCopies({
      product: payload.entityType,
      clientId,
      itemId: typeof fields.id === 'string' ? fields.id : undefined,
      fields,
      storedOwners,
    });
  };

  return { chooseCopiesToSaveOnto, saveOntoCopies, chooseCopiesToFold, foldCopies };
}
